<template>
  <div class="d-flex flex-column h-100">
    <!-- Header -->
    <slot name="header"></slot>
    <slot name="softwareCount"></slot>

    <!-- Refresh -->
    <GenericRefresh
      v-if="refreshData !== undefined"
      :initialCountdownTime="refreshData.initialCountdownTime"
      :shouldStartInterval="refreshData.shouldStartInterval"
      @onCountdownChange="handleOnCountdownChange"
      @onTimeoutCompleted="handleOnTimeoutCompleted"
    >
      <button
        slot="button"
        class="btn-refresh text-white py-1 px-0"
        :class="refreshData.shouldStartInterval ? 'counting' : 'loading'"
      >
        <div v-if="refreshData.shouldStartInterval" class="pl-2 pr-3">
          <i class="history icon"></i>
          <span>{{ currentTime }}</span>
        </div>
        <div v-else>
          <b-spinner small class="ml-auto" />
        </div>
      </button>
    </GenericRefresh>
    <div v-if="dataSet.length > 0" class="h-100 d-flex flex-column">
      <!-- Subheader -->
      <slot name="subheader"></slot>
      <!-- Columns -->
      <b-row align-h="between" no-gutters="">
        <b-col
          v-for="(column, i) in columnsMetaData"
          v-bind:key="i"
          :class="column.class"
        >
          <b-row align-h="between" no-gutters v-if="column.name !== ''">
            <b-col>
              <h6 :class="column.columnHeaderClass">
                {{ column.name }}
              </h6>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <!-- Data -->
      <div :class="scrollable ? 'scrollable-section' : ''">
        <b-row
          align-h="between"
          no-gutters
          v-for="(data, i) in dataSet"
          :key="i"
          class="my-1"
        >
          <b-col
            v-for="(value, j) in getColumnsValue(data)"
            :key="j"
            :class="getColumnDataClass(columnsMetaData[j])"
          >
            <component
              v-if="value.component !== undefined"
              v-bind:is="value.component.type"
              v-bind="value.component.props"
              :class="value.component.class"
            >
              {{ value.value }}
            </component>
            <p
              v-if="value.component === undefined"
              class="mb-0 pr-1"
              :class="{ 'text-truncate': !computedIsMobile }"
            >
              {{ value.value }}
            </p>
          </b-col>
        </b-row>
      </div>
      <!-- Pagination -->
      <div class="mt-auto">
        <Pagination
          class="mt-auto"
          :prevPageRequest="prevPageRequest"
          :nextPageRequest="nextPageRequest"
          @clicked="handleClickedPagination"
        ></Pagination>
      </div>
    </div>
    <div v-else class="placeholder my-auto py-5 rounded h-100">
      <h1>
        <i :class="noDataIcon"></i>
      </h1>
      <h3 class="font-weight-light text-center">
        {{ noDataMessage }}
      </h3>
    </div>
  </div>
</template>
<script>
import GenericRefresh from "./GenericRefresh.vue";
import Pagination from "../others/Pagination.vue";
/**
 * @description Permet l'affichage de données sous forme d'un tableau
 * @author Maxime Boisvert
 * @author Philippe Ménard
 * @vue-prop {Array} dataSet - Les données à afficher
 * @vue-prop {Object} binding - Les données qui permettent la liaison des données au tableau
 * @vue-prop {Array} columnsMetaData - Les données pour les colonnes du tableau
 * @vue-computed {Boolean} computedIsMobile - Détermine si l'écran est en mode mobile
 * @vue-prop {String} prevPageRequest - Le lien vers les données précédente
 * @vue-prop {String} nextPageRequest - Le lien vers les données suivantes
 * @vue-prop {Object} refreshData - Les données pour le décompte de rafraichissement
 * @vue-prop {String} noDataMessage - Le message à afficher lorsqu'il n'y a pas de données.  Si cette valeur n'est pas donnée, elle est vide ou undefined
 * le bouton précédent ne sera pas affiché
 * @vue-prop {String} noDataIcon - L'icône à afficher lorsqu'il n'y a pas de données. Si cette valeur n'est pas donnée, elle est vide ou undefined
 * le bouton précédent ne sera pas affiché
 * @vue-prop {Boolean} scrollable - Si la liste doit être défilable
 * @vue-data {Number} currentTime - Temps restant au compteur (en secondes)
 * @vue-data {Array} columnsValues - Les valeurs à afficher dans les colonnes
 * @vue-computed {Boolean} computedIsMobile - Si l'utilisateur est sur mobile ou pas
 * @vue-event {void} onTimeoutCompleted - Émet que le décompte est terminé
 * @vue-event {Number} onCountdownChange - Émet le temps qu'il reste au décompte
 * @vue-event {Object} paginateClicked - Émet que l'évènement « clicked » a été émis par un des boutons de pagination
 */
export default {
  name: "GenericList",
  components: {
    Pagination,
    GenericRefresh
  },
  props: {
    dataSet: {
      type: Array,
      required: true
    },
    binding: {
      type: Object,
      required: true
    },
    columnsMetaData: {
      type: Array,
      required: true
    },
    prevPageRequest: {
      type: String,
      required: false,
      default: () => ""
    },
    nextPageRequest: {
      type: String,
      required: false,
      default: () => ""
    },
    refreshData: {
      type: Object,
      required: false,
      default: () => undefined
    },
    noDataMessage: {
      type: String,
      required: false,
      default: function() {
        return this.$i18n.t("sections.GenericList.defaultNoDataMessage");
      }
    },
    noDataIcon: {
      type: String,
      required: false,
      default: () => "icon database"
    },
    scrollable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentTime: 0,
      columnsValues: []
    };
  },
  computed: {
    computedIsMobile() {
      return window.outerWidth <= 576;
    }
  },
  methods: {
    /**
     * @description Gère l'événement « onTimeoutCompleted » de la composante « GenericRefresh »
     */
    handleOnTimeoutCompleted() {
      this.$emit("onTimeoutCompleted");
    },
    /**
     * @description Gère l'événement « onCountdownChange » de la composante « GenericRefresh »
     * @param {Number} time Le temps qu'il reste au compteur en millisecondes
     */
    handleOnCountdownChange(time) {
      this.currentTime = time / 1000;
      this.$emit("onCountdownChange", time);
    },
    /**
     * @description Gère l'événement « paginateClicked » de la composante « Pagination »
     * @param {String} requestPage Lien à associer à l'évènement
     */
    handleClickedPagination(requestPage) {
      this.$emit("paginateClicked", requestPage);
    },
    /**
     * @description Obtient les classes pour les données dans les colonnes
     * @param {Array} columnsMetaData Les données pour les colonnes du tableau
     */
    getColumnDataClass(columnsMetaData) {
      let columnDataClass = columnsMetaData.columnDataClass ?? "";
      return columnsMetaData.class + " " + columnDataClass;
    },
    /**
     * @description Permet d'obtenir la composante d'un « dataBinding »
     * @param {Object} data L'objet lié dans cette colonne
     * @param {Object} bindingData Les données de la liaison pour la colonne
     * @returns {Object} Peut retourner undefined si aucune composante n'est définie pour cette liaison
     */
    getComponent(data, bindingData) {
      if (bindingData.component === undefined) {
        return undefined;
      }

      let props =
        bindingData.component.props !== undefined
          ? bindingData.component.props(data)
          : {};

      let propClass =
        bindingData.component.class !== undefined
          ? bindingData.component.class(data)
          : "";

      return {
        type: bindingData.component.type,
        props: props,
        class: propClass
      };
    },
    /**
     * @description Permet d'obtenir les valeurs des colonnes pour une ligne
     * @param {Object} data L'objet lié dans cette ligne
     * @returns {Array} Les valeurs des colonnes pour la ligne
     */
    getColumnsValue(data) {
      let values = [];
      let i = 0;
      for (const property in this.binding) {
        let value = {};
        if (this.columnsMetaData[i] !== undefined) {
          value.value =
            this.binding[property].transform === undefined
              ? data[property]
              : this.binding[property].transform(data);
          value.component = this.getComponent(data, this.binding[property]);
          values.push(value);
          i++;
        }
      }
      return values;
    }
  }
};
</script>
<style lang="scss" scoped>
.placeholder {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.btn-refresh {
  font-size: 13px;
  border: none;
  width: 5em;
  border-radius: 40px;

  &.counting {
    background: $gray-600;
  }

  &.loading {
    background: $gray-500;
  }
}

.hide-overflow {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scrollable-section {
  height: 25em;
  overflow: scroll;
}

.scrollable-section::-webkit-scrollbar {
  display: none;
}
</style>
